import React, { useEffect, useState, createContext, useContext } from 'react';
import { useMySchedule } from './MyScheduleContext';
interface Alert {
  id: string;
  type: 'session-reminder' | 'meeting-invite' | 'meeting-accepted' | 'meeting-declined' | 'chat-message' | 'session-starting' | 'event-update';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionable?: boolean;
  data?: any;
}
interface AlertsContextType {
  alerts: Alert[];
  unreadCount: number;
  actionableCount: number;
  markAsRead: (alertId: string) => void;
  markAllAsRead: () => void;
}
const AlertsContext = createContext<AlertsContextType | undefined>(undefined);
export function AlertsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    bookedSessions,
    meetings
  } = useMySchedule();
  // Generate initial alerts
  const generateAlerts = (): Alert[] => {
    const alerts: Alert[] = [];
    // Session reminders (for sessions happening soon)
    bookedSessions.slice(0, 3).forEach((session, index) => {
      alerts.push({
        id: `session-${session.id}`,
        type: 'session-reminder',
        title: 'Session Starting Soon',
        message: `"${session.title}" starts in 30 minutes at ${session.location}`,
        timestamp: `${30 + index * 15} minutes ago`,
        read: index > 0,
        data: {
          sessionId: session.id
        }
      });
    });
    // Meeting invites (pending meetings)
    meetings.filter(m => m.status === 'pending' && m.requestedBy === 'them').forEach((meeting, index) => {
      alerts.push({
        id: `meeting-invite-${meeting.id}`,
        type: 'meeting-invite',
        title: 'New Meeting Request',
        message: `${meeting.attendeeName} wants to meet on ${new Date(meeting.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })} at ${meeting.startTime}`,
        timestamp: `${index + 1} hour${index > 0 ? 's' : ''} ago`,
        read: false,
        actionable: true,
        data: {
          meetingId: meeting.id
        }
      });
    });
    // Meeting accepted notifications
    meetings.filter(m => m.status === 'confirmed' && m.requestedBy === 'me').slice(0, 2).forEach((meeting, index) => {
      alerts.push({
        id: `meeting-accepted-${meeting.id}`,
        type: 'meeting-accepted',
        title: 'Meeting Confirmed',
        message: `${meeting.attendeeName} accepted your meeting request for ${new Date(meeting.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })} at ${meeting.startTime}`,
        timestamp: `${2 + index} hours ago`,
        read: index > 0,
        data: {
          meetingId: meeting.id
        }
      });
    });
    // Mock chat messages
    alerts.push({
      id: 'chat-1',
      type: 'chat-message',
      title: 'New Message',
      message: 'Michael Johnson: "Looking forward to connecting at the event!"',
      timestamp: '2 hours ago',
      read: false,
      actionable: true,
      data: {
        chatId: 'chat-1',
        attendeeId: '2'
      }
    });
    // Session starting now
    if (bookedSessions.length > 0) {
      alerts.push({
        id: 'session-starting-1',
        type: 'session-starting',
        title: 'Session Starting Now',
        message: `"${bookedSessions[0].title}" is about to begin at ${bookedSessions[0].location}`,
        timestamp: '5 minutes ago',
        read: false,
        actionable: true,
        data: {
          sessionId: bookedSessions[0].id
        }
      });
    }
    // Event updates
    alerts.push({
      id: 'event-update-1',
      type: 'event-update',
      title: 'Event Highlight',
      message: "Don't miss the keynote presentation at Main Stage today at 2:00 PM",
      timestamp: '3 hours ago',
      read: true
    });
    alerts.push({
      id: 'event-update-2',
      type: 'event-update',
      title: 'New Exhibitor Added',
      message: 'Check out the latest exhibitor showcase in Hall B',
      timestamp: '5 hours ago',
      read: true
    });
    // Sort by timestamp (most recent first)
    return alerts.sort((a, b) => {
      const getMinutes = (str: string) => {
        const match = str.match(/(\d+)\s*(minute|hour|day)/);
        if (!match) return 0;
        const value = parseInt(match[1]);
        const unit = match[2];
        if (unit === 'minute') return value;
        if (unit === 'hour') return value * 60;
        if (unit === 'day') return value * 1440;
        return 0;
      };
      return getMinutes(a.timestamp) - getMinutes(b.timestamp);
    });
  };
  const [alerts, setAlerts] = useState<Alert[]>([]);
  // Initialize alerts
  useEffect(() => {
    setAlerts(generateAlerts());
  }, [bookedSessions, meetings]);
  const unreadCount = alerts.filter(a => !a.read).length;
  const actionableCount = alerts.filter(a => a.actionable).length;
  const markAsRead = (alertId: string) => {
    setAlerts(prev => prev.map(a => a.id === alertId ? {
      ...a,
      read: true
    } : a));
  };
  const markAllAsRead = () => {
    setAlerts(prev => prev.map(a => ({
      ...a,
      read: true
    })));
  };
  return <AlertsContext.Provider value={{
    alerts,
    unreadCount,
    actionableCount,
    markAsRead,
    markAllAsRead
  }} data-id="element-4265">
      {children}
    </AlertsContext.Provider>;
}
export function useAlerts() {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts must be used within AlertsProvider');
  }
  return context;
}